import React, { useEffect, useMemo, useState } from 'react';
import { Upload, Check, X, Loader2, ShoppingBag, AlertTriangle, EyeOff, Link2, Calendar, BarChart3, Table2, Wallet, Activity, Trophy, Store } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { salesAPI, sitesAPI, industrialProductsAPI, vitrineAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import PivotTab from './sales/PivotTab';
import GmailSyncCard from './sales/GmailSyncCard';
import FinancialTab from './sales/FinancialTab';
import ProductEvolutionTab from './sales/ProductEvolutionTab';
import TopProductsTab from './sales/TopProductsTab';
import VitrineTab from './sales/VitrineTab';
import SalesNotePopover from '../components/sales/SalesNotePopover';
import { useAuth } from '@/context/AuthContext';

const FE_SITES = ['FE Génipa', 'FE Cluny', 'FE Dillon', 'FE Océanis', 'FE Vauclin'];

export default function SalesPage() {
    const { hasPermission, user } = useAuth();
    // Per-tab permissions
    const canFinancial = hasPermission('sales_financial_view');
    const canEntries = hasPermission('sales_entries_view');
    const canEvolution = hasPermission('sales_evolution_view');
    const canTop = hasPermission('sales_top_view');
    const canPivot = hasPermission('sales_pivot_view');
    const canVitrine = hasPermission('sales_vitrine_view');
    const canImport = hasPermission('sales_import');
    const [sites, setSites] = useState([]);
    const [industrialProducts, setIndustrialProducts] = useState([]);
    const [vitrineAlerts, setVitrineAlerts] = useState(0);
    // Default tab = first one the user can access
    const defaultTab = canFinancial ? 'financial'
        : canEntries ? 'entries'
        : canEvolution ? 'evolution'
        : canTop ? 'top'
        : canPivot ? 'pivot'
        : canImport ? 'import'
        : 'financial';
    const [tab, setTab] = useState(defaultTab);
    // Import state
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [siteOverride, setSiteOverride] = useState('');
    // Shared filter state for Ventes tab (so CoverageGrid can drive it)
    const [entriesSiteId, setEntriesSiteId] = useState('all');
    // Default = current week (Monday -> Sunday), pre-selected in the week picker
    const [entriesStartDate, setEntriesStartDate] = useState(() => mondayOf(todayStr()));
    const [entriesEndDate, setEntriesEndDate] = useState(() => toIsoDate(addDays(new Date(mondayOf(todayStr()) + 'T00:00:00'), 6)));
    const [entriesViewMode, setEntriesViewMode] = useState('daily'); // 'cumul' | 'daily'

    useEffect(() => {
        sitesAPI.getAll().then(s => setSites(s.data || [])).catch(console.error);
        // Peut renvoyer 403 pour les profils sans products_view : ne doit pas bloquer les sites
        industrialProductsAPI.getAll().then(p => setIndustrialProducts(p.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!canVitrine) return;
        // Site-restricted users: no global anomaly dot (it aggregates other sites)
        if (user?.allowed_sites?.length) return;
        vitrineAPI.alerts({ days: 7 })
            .then(r => setVitrineAlerts(r.data?.recent_count || 0))
            .catch(() => {});
    }, [canVitrine, user]);

    const handleUpload = async () => {
        if (!file) return;
        setLoading(true);
        setPreview(null);
        try {
            const resp = await salesAPI.preview(file);
            setPreview(resp.data);
            setSiteOverride(resp.data.site_id || '');
            if (!resp.data.site_id) {
                toast.warning(`Site non reconnu (${resp.data.site_label || 'inconnu'}). Sélectionnez-le manuellement.`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de l\'analyse du fichier');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!preview) return;
        const finalSiteId = siteOverride || preview.site_id;
        if (!finalSiteId) {
            toast.error('Sélectionnez un site avant de valider');
            return;
        }
        setConfirming(true);
        try {
            const payload = {
                site_id: finalSiteId,
                site_label: preview.site_label,
                dates: preview.dates,
                format: preview.format,
                source_filename: preview.source_filename,
                products: preview.products,
            };
            const resp = await salesAPI.confirm(payload);
            toast.success(`Import terminé : ${resp.data.counters.entries_upserted} entrées enregistrées`);
            setPreview(null);
            setFile(null);
            setSiteOverride('');
            // Switch to Ventes tab to view results
            setTab('entries');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de l\'import');
        } finally {
            setConfirming(false);
        }
    };

    return (
        <div className="space-y-4 animate-fade-in" data-testid="sales-page">
            <div className="flex items-center gap-3">
                <ShoppingBag className="w-6 h-6 text-purple-500" />
                <h1 className="text-2xl font-bold text-slate-900">Ventes</h1>
                <span className="text-sm text-slate-500">Import des rapports de vente Fuji Express</span>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="flex flex-wrap h-auto gap-1 sm:inline-flex sm:flex-nowrap sm:h-9 sm:gap-0">
                    {canFinancial && <TabsTrigger value="financial" data-testid="tab-financial"><Wallet className="w-4 h-4 mr-1.5" />Financier</TabsTrigger>}
                    {canEntries && <TabsTrigger value="entries" data-testid="tab-entries"><BarChart3 className="w-4 h-4 mr-1.5" />Ventes</TabsTrigger>}
                    {canEvolution && <TabsTrigger value="evolution" data-testid="tab-evolution"><Activity className="w-4 h-4 mr-1.5" />Évolution produits</TabsTrigger>}
                    {canTop && <TabsTrigger value="top" data-testid="tab-top"><Trophy className="w-4 h-4 mr-1.5" />Top / Pareto</TabsTrigger>}
                    {canPivot && <TabsTrigger value="pivot" data-testid="tab-pivot"><Table2 className="w-4 h-4 mr-1.5" />TCD</TabsTrigger>}
                    {canVitrine && (
                        <TabsTrigger value="vitrine" data-testid="tab-vitrine" className="relative">
                            <Store className="w-4 h-4 mr-1.5" />Vitrine
                            {vitrineAlerts > 0 && (
                                <span
                                    className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"
                                    title={`${vitrineAlerts} anomalie(s) de prix sur les 7 derniers jours`}
                                    data-testid="vitrine-tab-alert-dot"
                                />
                            )}
                        </TabsTrigger>
                    )}

                    <TabsTrigger
                        value="aliases"
                        data-testid="tab-aliases"
                        disabled
                        title="Module Mappings en cours de développement"
                        className="opacity-40 cursor-not-allowed"
                    ><Link2 className="w-4 h-4 mr-1.5" />Mappings <span className="ml-1.5 text-[9px] uppercase font-semibold text-slate-400">bientôt</span></TabsTrigger>
                    {canImport && <TabsTrigger value="import" data-testid="tab-import"><Upload className="w-4 h-4 mr-1.5" />Import</TabsTrigger>}
                </TabsList>

                {/* forceMount on every TabsContent so that switching tabs preserves
                    the user's selections (filters, scope, scrolling, etc.) within the
                    same page visit. State is fully reset only when the user leaves
                    /sales and comes back (SalesPage unmounts/remounts). */}
                <TabsContent value="import" className="space-y-4" forceMount data-state={tab === 'import' ? 'active' : 'inactive'} hidden={tab !== 'import'}>
                    <GmailSyncCard onSynced={() => { /* CoverageGrid auto-refreshes via key */ }} />
                    <CoverageGrid
                        onCellClick={(site_id, monday, sunday) => {
                            setEntriesSiteId(site_id);
                            setEntriesStartDate(monday);
                            setEntriesEndDate(sunday);
                            setEntriesViewMode('daily');
                            setTab('entries');
                        }}
                    />
                    <ImportTab
                        file={file} setFile={setFile}
                        preview={preview}
                        loading={loading} confirming={confirming}
                        onUpload={handleUpload} onConfirm={handleConfirm}
                        sites={sites} siteOverride={siteOverride} setSiteOverride={setSiteOverride}
                        industrialProducts={industrialProducts}
                        setPreview={setPreview}
                    />
                </TabsContent>

                <TabsContent value="entries" forceMount data-state={tab === 'entries' ? 'active' : 'inactive'} hidden={tab !== 'entries'}>
                    <EntriesTab
                        sites={sites}
                        siteId={entriesSiteId} setSiteId={setEntriesSiteId}
                        startDate={entriesStartDate} setStartDate={setEntriesStartDate}
                        endDate={entriesEndDate} setEndDate={setEntriesEndDate}
                        viewMode={entriesViewMode} setViewMode={setEntriesViewMode}
                    />
                </TabsContent>

                <TabsContent value="pivot" forceMount data-state={tab === 'pivot' ? 'active' : 'inactive'} hidden={tab !== 'pivot'}>
                    <PivotTab sites={sites} active={tab === 'pivot'} />
                </TabsContent>

                <TabsContent value="financial" forceMount data-state={tab === 'financial' ? 'active' : 'inactive'} hidden={tab !== 'financial'}>
                    <FinancialTab />
                </TabsContent>

                <TabsContent value="evolution" forceMount data-state={tab === 'evolution' ? 'active' : 'inactive'} hidden={tab !== 'evolution'}>
                    <ProductEvolutionTab />
                </TabsContent>

                <TabsContent value="top" forceMount data-state={tab === 'top' ? 'active' : 'inactive'} hidden={tab !== 'top'}>
                    <TopProductsTab sites={sites} />
                </TabsContent>

                <TabsContent value="vitrine" forceMount data-state={tab === 'vitrine' ? 'active' : 'inactive'} hidden={tab !== 'vitrine'}>
                    <VitrineTab sites={sites} active={tab === 'vitrine'} />
                </TabsContent>


                <TabsContent value="aliases">
                    <AliasesTab industrialProducts={industrialProducts} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

// --------------- Import tab ---------------

function ImportTab({ file, setFile, preview, loading, confirming, onUpload, onConfirm, sites, siteOverride, setSiteOverride, industrialProducts, setPreview }) {
    const setProductStatus = (idx, status) => {
        const np = { ...preview };
        np.products = [...preview.products];
        np.products[idx] = { ...np.products[idx], status };
        if (status !== 'mapped') np.products[idx].industrial_product_id = null;
        setPreview(np);
    };
    const setProductMapping = (idx, industrialId) => {
        const np = { ...preview };
        np.products = [...preview.products];
        const ip = industrialProducts.find(p => p.id === industrialId);
        np.products[idx] = {
            ...np.products[idx],
            industrial_product_id: industrialId,
            industrial_product_name: ip?.name,
            status: industrialId ? 'mapped' : 'unmapped',
        };
        setPreview(np);
    };

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">1. Sélectionner le fichier Excel</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap items-center gap-3">
                        <Input
                            type="file"
                            accept=".xlsx"
                            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
                            data-testid="sales-file-input"
                            className="max-w-md"
                        />
                        <Button onClick={onUpload} disabled={!file || loading} data-testid="sales-upload-btn">
                            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                            Analyser
                        </Button>
                        {file && <span className="text-xs text-slate-500">{file.name}</span>}
                    </div>
                </CardContent>
            </Card>

            {preview && (
                <>
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                2. Confirmer le site et la période
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex flex-wrap items-center gap-3">
                                <div className="text-xs">
                                    <span className="text-slate-400">Détecté : </span>
                                    <span className="font-semibold">{preview.site_label || '?'}</span>
                                    <span className="text-slate-400"> → </span>
                                    <Badge variant="outline" className="text-xs">{preview.site_name || 'inconnu'}</Badge>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Label className="text-xs text-slate-500">Site final :</Label>
                                    <Select value={siteOverride} onValueChange={setSiteOverride}>
                                        <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="site-override-select">
                                            <SelectValue placeholder="Sélectionner..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {sites.filter(s => FE_SITES.includes(s.name)).map(s => (
                                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="text-xs text-slate-600 flex items-center gap-1">
                                    <Calendar className="w-3.5 h-3.5" />
                                    {preview.week_start} → {preview.week_end} ({preview.dates.length} jours)
                                </div>
                                {preview.has_demarque && (
                                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                        Démarque incluse
                                    </Badge>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-2 text-xs">
                                <Badge className="bg-emerald-100 text-emerald-700">{preview.counters.mapped} mappés</Badge>
                                <Badge className="bg-amber-100 text-amber-700">{preview.counters.unmapped} non mappés</Badge>
                                <Badge className="bg-slate-100 text-slate-600">{preview.counters.ignored} ignorés</Badge>
                                <Badge variant="outline">{preview.counters.products} total</Badge>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-base">3. Vérifier et importer</CardTitle>
                            <Button onClick={onConfirm} disabled={confirming || !siteOverride} className="bg-emerald-500 hover:bg-emerald-600" data-testid="confirm-import-btn">
                                {confirming ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                                Importer les ventes
                            </Button>
                        </CardHeader>
                        <CardContent className="overflow-x-auto p-0">
                            <table className="w-full text-xs">
                                <thead className="bg-slate-50 border-b">
                                    <tr>
                                        <th className="text-left p-2 w-24">Code Méti</th>
                                        <th className="text-left p-2">Libellé</th>
                                        <th className="text-right p-2 w-16">Qté</th>
                                        <th className="text-right p-2 w-20">CA</th>
                                        <th className="text-center p-2 w-24">Statut</th>
                                        <th className="text-left p-2 w-64">Produit Sokuzai</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.products.map((p, idx) => (
                                        <tr key={p.code_meti} className={`border-b ${p.status === 'ignored' ? 'bg-slate-50/60 opacity-50' : ''}`} data-testid={`preview-row-${p.code_meti}`}>
                                            <td className="p-2 font-mono">{p.code_meti}</td>
                                            <td className="p-2">{p.libelle}</td>
                                            <td className="p-2 text-right tabular-nums">{p.total_qty}</td>
                                            <td className="p-2 text-right tabular-nums">{formatCurrency(p.total_ca)}</td>
                                            <td className="p-2 text-center">
                                                {p.status === 'mapped' && <Badge className="bg-emerald-100 text-emerald-700">Mappé</Badge>}
                                                {p.status === 'unmapped' && <Badge className="bg-amber-100 text-amber-700">À mapper</Badge>}
                                                {p.status === 'ignored' && <Badge className="bg-slate-200 text-slate-600">Ignoré</Badge>}
                                            </td>
                                            <td className="p-2">
                                                <div className="flex items-center gap-1">
                                                    <Select
                                                        value={p.industrial_product_id || ''}
                                                        onValueChange={(v) => setProductMapping(idx, v)}
                                                        disabled={p.status === 'ignored'}
                                                    >
                                                        <SelectTrigger className="h-7 text-xs flex-1" data-testid={`map-select-${p.code_meti}`}>
                                                            <SelectValue placeholder={p.industrial_product_name || '— Lier à un produit —'} />
                                                        </SelectTrigger>
                                                        <SelectContent className="max-h-[280px]">
                                                            {industrialProducts.map(ip => (
                                                                <SelectItem key={ip.id} value={ip.id}>{ip.name}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    {p.status !== 'ignored' ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 px-2 text-slate-400 hover:text-red-600"
                                                            onClick={() => setProductStatus(idx, 'ignored')}
                                                            title="Ignorer ce produit"
                                                            data-testid={`ignore-btn-${p.code_meti}`}
                                                        >
                                                            <EyeOff className="w-3.5 h-3.5" />
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-7 px-2 text-slate-400 hover:text-emerald-600"
                                                            onClick={() => setProductStatus(idx, p.industrial_product_id ? 'mapped' : 'unmapped')}
                                                            title="Réactiver"
                                                            data-testid={`restore-btn-${p.code_meti}`}
                                                        >
                                                            <Check className="w-3.5 h-3.5" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </CardContent>
                    </Card>
                </>
            )}

            {!preview && !loading && (
                <Card>
                    <CardContent className="py-12 text-center text-slate-400 text-sm">
                        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        Aucun fichier analysé pour le moment. Sélectionnez votre rapport hebdomadaire ci-dessus.
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

// --------------- Entries (sales list) ---------------

function EntriesTab({ sites, siteId, setSiteId, startDate, setStartDate, endDate, setEndDate, viewMode, setViewMode }) {
    const [topMetric, setTopMetric] = useState('qty');
    const { user } = useAuth();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [notes, setNotes] = useState([]);

    // Site restriction: users with allowed_sites only see their own site(s)
    const allowedIds = user?.allowed_sites?.length ? user.allowed_sites : null;
    const feSites = useMemo(
        () => sites.filter(s => FE_SITES.includes(s.name) && (!allowedIds || allowedIds.includes(s.id))),
        [sites, allowedIds]
    );
    const singleSite = !!allowedIds && feSites.length === 1;

    useEffect(() => {
        if (singleSite && siteId !== feSites[0].id) setSiteId(feSites[0].id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singleSite, feSites]);

    const load = async () => {
        setLoading(true);
        try {
            const params = { start_date: startDate, end_date: endDate, limit: 10000 };
            if (siteId !== 'all') params.site_id = siteId;
            else params.site_ids = feSites.map(s => s.id).join(',');
            const resp = await salesAPI.listEntries(params);
            setEntries(resp.data || []);
        } catch (e) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    const loadNotes = async () => {
        try {
            const params = { start_date: startDate, end_date: endDate };
            if (siteId !== 'all') params.site_id = siteId;
            const r = await salesAPI.listNotes(params);
            let list = r.data || [];
            if (allowedIds) list = list.filter(n => !n.site_id || allowedIds.includes(n.site_id));
            setNotes(list);
        } catch (e) { /* silent */ }
    };

    useEffect(() => { if (feSites.length) { load(); loadNotes(); } /* eslint-disable-next-line */ }, [feSites.length, siteId, startDate, endDate]);

    const grouped = useMemo(() => {
        const map = new Map();
        for (const e of entries) {
            const k = `${e.code_meti}|${e.libelle_meti}|${e.site_id}`;
            if (!map.has(k)) {
                map.set(k, {
                    code_meti: e.code_meti, libelle: e.libelle_meti, site_id: e.site_id, site_name: e.site_name,
                    qty: 0, ca: 0, qty_loss: 0, value_loss: 0,
                });
            }
            const g = map.get(k);
            g.qty += e.qty; g.ca += e.ca; g.qty_loss += e.qty_loss || 0; g.value_loss += e.value_loss || 0;
        }
        return [...map.values()].sort((a, b) => b.ca - a.ca);
    }, [entries]);

    const topList = useMemo(() => {
        if (viewMode !== 'top') return [];
        return [...grouped].sort((a, b) => topMetric === 'qty' ? b.qty - a.qty : b.ca - a.ca);
    }, [grouped, viewMode, topMetric]);

    const totals = useMemo(() => grouped.reduce((acc, g) => ({
        qty: acc.qty + g.qty, ca: acc.ca + g.ca, qty_loss: acc.qty_loss + g.qty_loss, value_loss: acc.value_loss + g.value_loss,
    }), { qty: 0, ca: 0, qty_loss: 0, value_loss: 0 }), [grouped]);

    const handleDeleteDay = async (date) => {
        if (siteId === 'all') return;
        const site = sites.find(s => s.id === siteId);
        const count = entries.filter(e => e.date === date).length;
        if (!window.confirm(`Effacer toutes les ventes du ${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)} pour ${site?.name} ?\n\nCela supprimera ${count} ligne(s) de vente. Cette action est irréversible.`)) {
            return;
        }
        try {
            const resp = await salesAPI.deleteEntries({ site_id: siteId, date });
            toast.success(`${resp.data.deleted} ligne(s) supprimée(s)`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de suppression');
        }
    };

    // Daily pivot: rows = product (code_meti + libelle), columns = ALL dates in range (incl. closed days), cells = qty (and CA tooltip)
    const dailyPivot = useMemo(() => {
        if (viewMode !== 'daily') return null;
        // Build full date list from startDate→endDate so closed days appear as empty columns
        const dates = [];
        try {
            const s = new Date(startDate + 'T00:00:00');
            const e = new Date(endDate + 'T00:00:00');
            for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
                dates.push(d.toISOString().slice(0, 10));
            }
        } catch (_e) { /* empty */ }
        const products = new Map(); // key -> { code_meti, libelle, site_name, days: {date: {qty, ca}}, total: {qty,ca} }
        for (const e of entries) {
            const k = `${e.code_meti}|${e.site_id}`;
            if (!products.has(k)) {
                products.set(k, {
                    code_meti: e.code_meti, libelle: e.libelle_meti, site_name: e.site_name,
                    days: {}, total: { qty: 0, ca: 0 },
                });
            }
            const p = products.get(k);
            const prev = p.days[e.date] || { qty: 0, ca: 0 };
            prev.qty += e.qty; prev.ca += e.ca;
            p.days[e.date] = prev;
            p.total.qty += e.qty; p.total.ca += e.ca;
        }
        const productList = [...products.values()].sort((a, b) => b.total.ca - a.total.ca);
        // Column totals
        const colTotals = {};
        for (const d of dates) {
            colTotals[d] = { qty: 0, ca: 0 };
            for (const p of productList) {
                const v = p.days[d];
                if (v) { colTotals[d].qty += v.qty; colTotals[d].ca += v.ca; }
            }
        }
        return { dates, products: productList, colTotals };
    }, [entries, viewMode, startDate, endDate]);

    return (
        <Card>
            <CardHeader>
                <div className="flex flex-wrap items-center gap-3">
                    {/* Sites shortcuts */}
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="sales-site-shortcuts">
                        {!singleSite && (
                            <button
                                type="button"
                                onClick={() => setSiteId('all')}
                                className={`px-3 h-7 rounded-full text-xs font-semibold border ${siteId === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}
                            >Tous FE</button>
                        )}
                        {feSites.map(s => {
                            const short = s.name.replace('FE ', '');
                            const active = siteId === s.id;
                            return (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => { if (!singleSite) setSiteId(active ? 'all' : s.id); }}
                                    className={`px-3 h-7 rounded-full text-xs font-semibold border ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}
                                    data-testid={`sales-site-${short.toLowerCase()}`}
                                >{short}</button>
                            );
                        })}
                    </div>
                    {/* View mode toggle */}
                    <div className="inline-flex bg-slate-200 rounded-md p-0.5 text-xs gap-0.5">
                        <button
                            type="button"
                            onClick={() => setViewMode('cumul')}
                            className={`px-3 py-1 rounded font-semibold transition-all ${viewMode === 'cumul' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
                            data-testid="view-cumul-btn"
                        >Cumul</button>
                        <button
                            type="button"
                            onClick={() => setViewMode('top')}
                            className={`px-3 py-1 rounded font-semibold transition-all ${viewMode === 'top' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
                            data-testid="view-top-btn"
                        >Top Ventes</button>
                        <button
                            type="button"
                            onClick={() => setViewMode('daily')}
                            className={`px-3 py-1 rounded font-semibold transition-all ${viewMode === 'daily' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}
                            data-testid="view-daily-btn"
                        >Par jour</button>
                    </div>
                </div>
                {/* Week picker (visual selection of weeks to consult) */}
                <div className="mt-2 flex items-end gap-2">
                    <div className="flex-1 min-w-0">
                        <WeekPicker
                            siteId={siteId}
                            feSites={feSites}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                        />
                    </div>
                    <SalesNotePopover
                        dateIso={endDate}
                        siteId={siteId !== 'all' ? siteId : null}
                        siteName={siteId !== 'all' ? sites.find(s => s.id === siteId)?.name : null}
                        sites={feSites}
                        notes={notes}
                        onChanged={loadNotes}
                        variant="button"
                        label="Nouvelle note"
                    />
                </div>
                <div className="flex gap-3 mt-2 text-xs items-center">
                    <Badge variant="outline">{viewMode === 'daily' ? `${dailyPivot?.products.length || 0} produits × ${dailyPivot?.dates.length || 0} jours` : `${grouped.length} produits`}</Badge>
                    <Badge className="bg-emerald-100 text-emerald-700">CA : {formatCurrency(totals.ca)}</Badge>
                    <Badge className="bg-blue-100 text-blue-700">Qté : {totals.qty.toFixed(0)}</Badge>
                    {totals.value_loss > 0 && (
                        <Badge className="bg-amber-100 text-amber-700">Démarque : {formatCurrency(totals.value_loss)}</Badge>
                    )}
                    {notes.length > 0 && (
                        <Badge
                            className="bg-amber-50 text-amber-700 border border-amber-200 cursor-help"
                            title={notes.map(n => `${n.date}${n.site_name ? ' · ' + n.site_name.replace('FE ', '') : ''} — ${n.text}`).join('\n')}
                            data-testid="entries-notes-badge"
                        >
                            📝 {notes.length} note{notes.length > 1 ? 's' : ''}
                        </Badge>
                    )}
                </div>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
                {loading ? (
                    <div className="py-8 text-center text-slate-400 text-sm">
                        <Loader2 className="w-6 h-6 mx-auto animate-spin" />
                    </div>
                ) : viewMode === 'cumul' ? (
                    grouped.length === 0 ? (
                        <div className="py-8 text-center text-slate-400 text-sm">Aucune vente sur cette période.</div>
                    ) : (
                        <table className="w-full text-[11px]">
                            <thead className="bg-slate-50 border-b sticky top-0 z-10">
                                <tr className="text-[10px] uppercase text-slate-500">
                                    <th className="text-left px-2 py-1 w-20">Code Méti</th>
                                    <th className="text-left px-2 py-1">Libellé</th>
                                    <th className="text-left px-2 py-1 w-24">Site</th>
                                    <th className="text-right px-2 py-1 w-14">Qté</th>
                                    <th className="text-right px-2 py-1 w-20">CA</th>
                                    <th className="text-right px-2 py-1 w-20">Démarque</th>
                                </tr>
                            </thead>
                            <tbody>
                                {grouped.map((g, i) => (
                                    <tr key={`${g.code_meti}-${g.site_id}-${i}`} className="odd:bg-white even:bg-slate-200 hover:bg-blue-50 border-b border-slate-100">
                                        <td className="px-2 py-0.5 font-mono text-slate-500">{g.code_meti}</td>
                                        <td className="px-2 py-0.5 truncate max-w-[420px]" title={g.libelle}>{g.libelle}</td>
                                        <td className="px-2 py-0.5 text-slate-500">{g.site_name?.replace('FE ', '')}</td>
                                        <td className="px-2 py-0.5 text-right tabular-nums">{g.qty.toFixed(0)}</td>
                                        <td className="px-2 py-0.5 text-right tabular-nums font-semibold">{formatCurrency(g.ca)}</td>
                                        <td className="px-2 py-0.5 text-right tabular-nums text-amber-700">
                                            {g.value_loss > 0 ? formatCurrency(g.value_loss) : <span className="text-slate-200">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                ) : viewMode === 'top' ? (
                    topList.length === 0 ? (
                        <div className="py-8 text-center text-slate-400 text-sm">Aucune vente sur cette période.</div>
                    ) : (() => {
                        const maxVal = topMetric === 'qty' ? topList[0].qty : topList[0].ca;
                        return (
                            <div data-testid="top-sales-view">
                                <div className="flex items-center gap-2 px-3 py-2 border-b bg-slate-50">
                                    <span className="text-[11px] text-slate-500 font-semibold uppercase">Classé par</span>
                                    <div className="inline-flex bg-slate-200 rounded-md p-0.5 text-xs gap-0.5">
                                        <button type="button" onClick={() => setTopMetric('qty')}
                                            className={`px-3 py-0.5 rounded font-semibold transition-all ${topMetric === 'qty' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}
                                            data-testid="top-metric-qty-btn">Quantité</button>
                                        <button type="button" onClick={() => setTopMetric('ca')}
                                            className={`px-3 py-0.5 rounded font-semibold transition-all ${topMetric === 'ca' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}
                                            data-testid="top-metric-ca-btn">CA</button>
                                    </div>
                                    <span className="text-[11px] text-slate-400">ordre décroissant</span>
                                </div>
                                <table className="w-full text-[11px]">
                                    <thead className="bg-slate-50 border-b sticky top-0 z-10">
                                        <tr className="text-[10px] uppercase text-slate-500">
                                            <th className="text-center px-2 py-1 w-10">#</th>
                                            <th className="text-left px-2 py-1 w-20">Code Méti</th>
                                            <th className="text-left px-2 py-1">Libellé</th>
                                            <th className="text-left px-2 py-1 w-24">Site</th>
                                            <th className="text-right px-2 py-1 w-16">Qté</th>
                                            <th className="text-right px-2 py-1 w-24">CA</th>
                                            <th className="text-left px-2 py-1 w-[200px]"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topList.map((g, i) => {
                                            const val = topMetric === 'qty' ? g.qty : g.ca;
                                            const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
                                            const medal = i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-slate-400 text-white' : i === 2 ? 'bg-orange-700 text-white' : 'bg-slate-100 text-slate-500';
                                            return (
                                                <tr key={`${g.code_meti}-${g.site_id}-${i}`} className="odd:bg-white even:bg-slate-50 hover:bg-blue-50 border-b border-slate-100" data-testid={`top-sales-row-${i + 1}`}>
                                                    <td className="px-2 py-0.5 text-center">
                                                        <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${medal}`}>{i + 1}</span>
                                                    </td>
                                                    <td className="px-2 py-0.5 font-mono text-slate-500">{g.code_meti}</td>
                                                    <td className="px-2 py-0.5 truncate max-w-[360px]" title={g.libelle}>{g.libelle}</td>
                                                    <td className="px-2 py-0.5 text-slate-500">{g.site_name?.replace('FE ', '')}</td>
                                                    <td className={`px-2 py-0.5 text-right tabular-nums ${topMetric === 'qty' ? 'font-bold' : ''}`}>{g.qty.toFixed(0)}</td>
                                                    <td className={`px-2 py-0.5 text-right tabular-nums ${topMetric === 'ca' ? 'font-bold' : 'font-semibold'}`}>{formatCurrency(g.ca)}</td>
                                                    <td className="px-2 py-0.5">
                                                        <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                                                            <div className={`h-full rounded-full ${i < 3 ? 'bg-emerald-500' : 'bg-slate-400'}`} style={{ width: `${pct}%` }} />
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })()
                ) : (
                    // DAILY pivot view
                    !dailyPivot || dailyPivot.dates.length === 0 ? (
                        <div className="py-8 text-center text-slate-400 text-sm">Aucune vente sur cette période.</div>
                    ) : (
                        <table className="w-full text-[11px]" data-testid="daily-pivot-table">
                            <thead className="bg-slate-50 border-b sticky top-0 z-20">
                                <tr>
                                    <th className="text-left px-2 py-1 sticky left-0 bg-slate-50 z-10 w-20 text-[10px] uppercase text-slate-500">Code</th>
                                    <th className="text-left px-2 py-1 sticky left-20 bg-slate-50 z-10 min-w-[200px] text-[10px] uppercase text-slate-500">Libellé</th>
                                    {siteId === 'all' && <th className="text-left px-2 py-1 w-20 text-[10px] uppercase text-slate-500">Site</th>}
                                    {dailyPivot.dates.map(d => (
                                        <th key={d} className="text-center px-1 py-0.5 text-[10px] font-semibold text-slate-500 whitespace-nowrap leading-tight group">
                                            <div className="flex items-center justify-center gap-1.5">
                                                <span>{d.slice(8, 10)}/{d.slice(5, 7)}</span>
                                                <SalesNotePopover
                                                    dateIso={d}
                                                    siteId={siteId !== 'all' ? siteId : null}
                                                    siteName={siteId !== 'all' ? sites.find(s => s.id === siteId)?.name : null}
                                                    sites={feSites}
                                                    notes={notes}
                                                    onChanged={loadNotes}
                                                    size="xs"
                                                />
                                                {siteId !== 'all' && !notes.some(n => n.date === d && (!n.site_id || n.site_id === siteId)) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteDay(d)}
                                                        className="text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                                                        title={`Effacer les ventes du ${d}`}
                                                        data-testid={`delete-day-${d}`}
                                                    >
                                                        <X className="w-2.5 h-2.5" />
                                                    </button>
                                                )}
                                            </div>
                                            {(() => {
                                                const dayIdx = new Date(d + 'T00:00:00').getDay();
                                                const letter = ['D', 'L', 'M', 'M', 'J', 'V', 'S'][dayIdx];
                                                const isWeekend = dayIdx === 0 || dayIdx === 6;
                                                return (
                                                    <div className={`text-[9px] font-normal ${isWeekend ? 'text-red-400 font-semibold' : 'text-slate-300'}`}>{letter}</div>
                                                );
                                            })()}
                                        </th>
                                    ))}
                                    <th className="text-right px-2 py-1 bg-slate-100 sticky right-0 z-10 text-[10px] uppercase text-slate-500">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dailyPivot.products.map((p, idx) => {
                                    const bg = idx % 2 === 0 ? 'bg-white' : 'bg-slate-200';
                                    return (
                                    <tr key={`${p.code_meti}-${p.site_name}`} className={`${bg} hover:bg-blue-50 border-b border-slate-100`}>
                                        <td className={`px-2 py-0.5 font-mono text-slate-500 sticky left-0 ${bg}`}>{p.code_meti}</td>
                                        <td className={`px-2 py-0.5 sticky left-20 truncate max-w-[320px] ${bg}`} title={p.libelle}>{p.libelle}</td>
                                        {siteId === 'all' && <td className="px-2 py-0.5 text-slate-500">{p.site_name?.replace('FE ', '')}</td>}
                                        {dailyPivot.dates.map(d => {
                                            const v = p.days[d];
                                            return (
                                                <td key={d} className="px-1 py-0.5 text-center tabular-nums">
                                                    {v ? (
                                                        <span className="font-semibold text-slate-800" title={`${v.qty} unités — ${formatCurrency(v.ca)}`}>
                                                            {v.qty.toFixed(0)}
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-200">·</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                        <td className="px-2 py-0.5 text-right tabular-nums font-bold bg-slate-300 sticky right-0 whitespace-nowrap">
                                            <span>{p.total.qty.toFixed(0)}</span>
                                            <span className="text-[9px] text-slate-600 font-normal ml-1">{formatCurrency(p.total.ca)}</span>
                                        </td>
                                    </tr>
                                    );
                                })}
                                {/* Footer totals row */}
                                <tr className="bg-slate-300 border-t-2 border-slate-400">
                                    <td className="px-2 py-1 font-bold sticky left-0 bg-slate-300" colSpan={siteId === 'all' ? 3 : 2}>Total jour</td>
                                    {dailyPivot.dates.map(d => {
                                        const t = dailyPivot.colTotals[d] || { qty: 0, ca: 0 };
                                        return (
                                            <td key={d} className="px-1 py-0.5 text-center tabular-nums font-bold leading-tight" title={formatCurrency(t.ca)}>
                                                <div className="text-slate-800">{t.qty.toFixed(0)}</div>
                                                <div className="text-[10px] text-emerald-600 font-semibold">{formatCurrency(t.ca)}</div>
                                            </td>
                                        );
                                    })}
                                    <td className="px-2 py-1 text-right tabular-nums font-bold bg-slate-400 sticky right-0 whitespace-nowrap text-white">
                                        <span>{totals.qty.toFixed(0)}</span>
                                        <span className="text-[11px] text-slate-200 font-semibold ml-1">{formatCurrency(totals.ca)}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )
                )}
            </CardContent>
        </Card>
    );
}

// --------------- Aliases tab ---------------

function AliasesTab({ industrialProducts }) {
    const [aliases, setAliases] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await salesAPI.listAliases();
            setAliases(r.data || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const setMapping = async (code, industrial_product_id, libelle) => {
        try {
            await salesAPI.upsertAlias(code, { industrial_product_id, libelle });
            toast.success('Mapping enregistré');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };
    const setIgnored = async (code, libelle) => {
        try {
            await salesAPI.upsertAlias(code, { ignored: true, libelle });
            toast.success('Code ignoré');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    };
    const remove = async (code) => {
        if (!window.confirm(`Supprimer le mapping ${code} ?`)) return;
        await salesAPI.deleteAlias(code);
        toast.success('Mapping supprimé');
        load();
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Mappings Code Méti → Produit Sokuzai</CardTitle>
                <p className="text-xs text-slate-500">Ces correspondances sont automatiquement appliquées aux imports suivants.</p>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
                {loading ? (
                    <div className="py-8 text-center text-slate-400 text-sm"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
                ) : aliases.length === 0 ? (
                    <div className="py-8 text-center text-slate-400 text-sm">Aucun mapping pour le moment.</div>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 border-b">
                            <tr>
                                <th className="text-left p-2 w-24">Code Méti</th>
                                <th className="text-left p-2">Libellé Méti</th>
                                <th className="text-left p-2">Produit Sokuzai</th>
                                <th className="text-center p-2 w-24">État</th>
                                <th className="text-right p-2 w-24">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {aliases.map(a => (
                                <tr key={a.code_meti} className="border-b">
                                    <td className="p-2 font-mono">{a.code_meti}</td>
                                    <td className="p-2 text-slate-700">{a.libelle_seen || '—'}</td>
                                    <td className="p-2">
                                        <Select
                                            value={a.industrial_product_id || ''}
                                            onValueChange={(v) => setMapping(a.code_meti, v, a.libelle_seen)}
                                            disabled={a.ignored}
                                        >
                                            <SelectTrigger className="h-7 text-xs w-[260px]">
                                                <SelectValue placeholder={a.industrial_product_name || '— Lier —'} />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-[280px]">
                                                {industrialProducts.map(ip => (
                                                    <SelectItem key={ip.id} value={ip.id}>{ip.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="p-2 text-center">
                                        {a.ignored ? (
                                            <Badge className="bg-slate-200 text-slate-700">Ignoré</Badge>
                                        ) : a.industrial_product_id ? (
                                            <Badge className="bg-emerald-100 text-emerald-700">Mappé</Badge>
                                        ) : (
                                            <Badge className="bg-amber-100 text-amber-700">Vide</Badge>
                                        )}
                                    </td>
                                    <td className="p-2 text-right">
                                        {!a.ignored && (
                                            <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-400" onClick={() => setIgnored(a.code_meti, a.libelle_seen)}>
                                                <EyeOff className="w-3.5 h-3.5" />
                                            </Button>
                                        )}
                                        <Button size="sm" variant="ghost" className="h-7 px-2 text-red-400" onClick={() => remove(a.code_meti)}>
                                            <X className="w-3.5 h-3.5" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </CardContent>
        </Card>
    );
}

// Helpers
// --------------- Week Picker (visual week selector with month shortcuts) ---------------

const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
// Fixed widths so the month shortcuts align with their underlying weeks
const WP_WEEK_W = 52; // px
const WP_WEEK_GAP = 2; // px

function WeekPicker({ siteId, feSites, startDate, endDate, onChange }) {
    const [coverage, setCoverage] = useState(null);
    const scrollerRef = React.useRef(null);
    const [anchor, setAnchor] = useState(null); // last single-click for shift-range

    // Build the list of week Mondays from November 2020 (début historique) to today + 4 weeks
    const weeks = useMemo(() => {
        const today = new Date();
        let start = new Date(2020, 10, 1);
        const dow = start.getDay();
        const diff = dow === 0 ? -6 : 1 - dow;
        start.setDate(start.getDate() + diff);
        let end = new Date(today);
        const tdow = end.getDay();
        const tdiff = tdow === 0 ? -6 : 1 - tdow;
        end.setDate(end.getDate() + tdiff + 7 * 4);
        const ws = [];
        let cur = new Date(start);
        while (cur <= end) { ws.push(new Date(cur)); cur.setDate(cur.getDate() + 7); }
        return ws;
    }, []);

    // Group consecutive weeks by their "principal" month (ISO: month of Thursday)
    const monthGroups = useMemo(() => {
        const groups = [];
        let current = null;
        for (let i = 0; i < weeks.length; i++) {
            const mon = weeks[i];
            const thu = addDays(mon, 3); // ISO week month rule
            const y = thu.getFullYear();
            const m = thu.getMonth() + 1; // 1..12
            const key = `${y}-${m}`;
            if (!current || current.key !== key) {
                current = { key, year: y, month: m, startIdx: i, nWeeks: 1 };
                groups.push(current);
            } else {
                current.nWeeks += 1;
            }
        }
        return groups;
    }, [weeks]);

    const rangeStartIso = useMemo(() => weeks.length ? toIsoDate(weeks[0]) : null, [weeks]);
    const rangeEndIso = useMemo(() => weeks.length ? toIsoDate(addDays(weeks[weeks.length - 1], 6)) : null, [weeks]);

    // Fetch coverage to display per-week color hint
    useEffect(() => {
        if (!rangeStartIso || !rangeEndIso) return;
        (async () => {
            try {
                const r = await salesAPI.coverage({ start_date: rangeStartIso, end_date: rangeEndIso });
                setCoverage(r.data);
            } catch (e) { /* silent */ }
        })();
    }, [rangeStartIso, rangeEndIso]);

    // Build a map: weekIso(Mon) -> Set(date) for current site (or all FE)
    const coverageMap = useMemo(() => {
        if (!coverage) return null;
        const targetIds = siteId === 'all'
            ? new Set(feSites.map(s => s.id))
            : new Set([siteId]);
        const m = {};
        for (const [sid, d] of coverage.pairs) {
            if (!targetIds.has(sid)) continue;
            const monIso = mondayOf(d);
            if (!m[monIso]) m[monIso] = new Set();
            m[monIso].add(d);
        }
        return m;
    }, [coverage, siteId, feSites]);

    const selectedMon = startDate ? mondayOf(startDate) : null;
    const selectedEndMon = endDate ? mondayOf(endDate) : null;

    const isSelected = (monIso) => {
        if (!selectedMon || !selectedEndMon) return false;
        return monIso >= selectedMon && monIso <= selectedEndMon;
    };

    // Detect which month is "currently selected" (covers whole month exactly)
    const selectedMonthKey = useMemo(() => {
        if (!startDate || !endDate) return null;
        if (startDate.slice(8, 10) !== '01') return null;
        const [y, m] = startDate.split('-').map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        const expectedEnd = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        if (endDate !== expectedEnd) return null;
        return `${y}-${m}`;
    }, [startDate, endDate]);

    const handleMonthClick = (year, monthIdx) => {
        const lastDay = new Date(year, monthIdx, 0).getDate();
        const s = `${year}-${String(monthIdx).padStart(2, '0')}-01`;
        const e = `${year}-${String(monthIdx).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        onChange(s, e);
    };

    // Auto-scroll to the selected (or current) week. The picker can mount inside
    // a hidden tab (forceMount), so wait until it actually has a width.
    useEffect(() => {
        const el = scrollerRef.current;
        if (!el || !weeks.length) return;
        const targetIso = (startDate && mondayOf(startDate)) || mondayOf(toIsoDate(new Date()));
        const idx = weeks.findIndex(w => toIsoDate(w) === targetIso);
        if (idx < 0) return;
        const position = () => { el.scrollLeft = Math.max(0, (idx - 6) * (WP_WEEK_W + WP_WEEK_GAP)); };
        if (el.clientWidth > 0) { position(); return; }
        const ro = new ResizeObserver(() => {
            if (el.clientWidth > 0) { position(); ro.disconnect(); }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [weeks]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClick = (mon, e) => {
        const monIso = toIsoDate(mon);
        const sun = addDays(mon, 6);
        const sunIso = toIsoDate(sun);
        if (e.shiftKey && anchor) {
            // Range from anchor to clicked
            const a = anchor;
            const b = monIso;
            const lo = a < b ? a : b;
            const hi = a < b ? b : a;
            const hiSun = toIsoDate(addDays(new Date(hi + 'T00:00:00'), 6));
            onChange(lo, hiSun);
        } else {
            setAnchor(monIso);
            onChange(monIso, sunIso);
        }
    };

    const goPrev = () => { if (scrollerRef.current) scrollerRef.current.scrollLeft -= 200; };
    const goNext = () => { if (scrollerRef.current) scrollerRef.current.scrollLeft += 200; };

    const todayY = new Date().getFullYear();
    const todayM = new Date().getMonth() + 1;
    const isFuture = (y, m) => (y > todayY) || (y === todayY && m > todayM);
    const isCurrentMonth = (y, m) => (y === todayY && m === todayM);

    return (
        <div className="flex items-center gap-1 bg-slate-50 rounded-lg border border-slate-200 p-1">
            <button onClick={goPrev} className="h-14 w-6 flex-shrink-0 text-slate-400 hover:text-slate-700" title="Précédent">‹</button>
            <div ref={scrollerRef} className="flex-1 overflow-x-auto" style={{ scrollBehavior: 'smooth' }}>
                <div className="min-w-max" data-testid="sales-month-shortcuts">
                    {/* Months row */}
                    <div className="flex" style={{ gap: `${WP_WEEK_GAP}px` }}>
                        {monthGroups.map((g) => {
                            const isActive = selectedMonthKey === g.key;
                            const isCur = isCurrentMonth(g.year, g.month);
                            const isFut = isFuture(g.year, g.month);
                            const width = g.nWeeks * WP_WEEK_W + (g.nWeeks - 1) * WP_WEEK_GAP;
                            return (
                                <button
                                    key={g.key}
                                    type="button"
                                    onClick={() => handleMonthClick(g.year, g.month)}
                                    title={`${MONTHS_FR[g.month - 1]} ${g.year}`}
                                    disabled={isFut}
                                    className={`shrink-0 h-5 mb-0.5 rounded border text-[10px] font-semibold transition-colors leading-none ${
                                        isActive
                                            ? 'bg-slate-900 text-white border-slate-900'
                                            : isCur
                                                ? 'bg-blue-50 text-blue-700 border-blue-300'
                                                : isFut
                                                    ? 'bg-white text-slate-300 border-slate-200'
                                                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                                    }`}
                                    style={{ width: `${width}px` }}
                                    data-testid={`sales-month-${g.year}-${g.month}`}
                                >
                                    {MONTHS_FR[g.month - 1]}
                                    {g.month === 1 && <span className="ml-1 opacity-50">{g.year}</span>}
                                </button>
                            );
                        })}
                    </div>
                    {/* Weeks row */}
                    <div className="flex" style={{ gap: `${WP_WEEK_GAP}px` }}>
                        {weeks.map((mon, i) => {
                            const monIso = toIsoDate(mon);
                            const sun = addDays(mon, 6);
                            const daysSet = coverageMap?.[monIso];
                            const count = daysSet ? daysSet.size : 0;
                            const isCur = sameWeek(mon, new Date());
                            const status = count === 0 ? 'missing' : count >= 7 ? 'full' : 'partial';
                            const selected = isSelected(monIso);
                            const dotClass = status === 'full'
                                ? 'bg-emerald-500'
                                : status === 'partial'
                                    ? 'bg-amber-400'
                                    : 'bg-slate-300';
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={(e) => handleClick(mon, e)}
                                    title={`Semaine ${isoWeekNumber(mon)} — ${monIso} → ${toIsoDate(sun)}${count > 0 ? ` (${count}/7 jours)` : ''}\nShift+clic = sélectionner une plage`}
                                    className={`shrink-0 py-1 rounded-md border text-[10px] leading-tight text-center transition-colors ${
                                        selected
                                            ? 'bg-slate-900 text-white border-slate-900'
                                            : isCur
                                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                    }`}
                                    style={{ width: `${WP_WEEK_W}px` }}
                                    data-testid={`week-pick-${monIso}`}
                                >
                                    <div className="font-bold">S{isoWeekNumber(mon)}</div>
                                    <div className={`text-[9px] ${selected ? 'text-slate-300' : 'text-slate-400'}`}>
                                        {String(mon.getDate()).padStart(2, '0')}/{String(mon.getMonth() + 1).padStart(2, '0')}
                                    </div>
                                    <div className="flex justify-center mt-0.5">
                                        <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
            <button onClick={goNext} className="h-14 w-6 flex-shrink-0 text-slate-400 hover:text-slate-700" title="Suivant">›</button>
        </div>
    );
}

function addDays(d, n) {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
}

// --------------- Coverage Grid (weekly heatmap) ---------------

function CoverageGrid({ onCellClick }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const scrollerRef = React.useRef(null);

    // Range: start = first Monday of the current year, end = +4 weeks from today
    const { startDate, endDate, weeks } = useMemo(() => {
        const today = new Date();
        // First Monday of November 2020 (début historique ventes)
        let start = new Date(2020, 10, 1);
        const dow = start.getDay(); // 0=Sun
        const diff = dow === 0 ? -6 : 1 - dow;
        start.setDate(start.getDate() + diff);
        // End = Monday of today + 4 weeks
        let end = new Date(today);
        const tdow = end.getDay();
        const tdiff = tdow === 0 ? -6 : 1 - tdow;
        end.setDate(end.getDate() + tdiff + 7 * 4 + 6); // Sunday of week + 4
        // Build all weeks (Monday entries)
        const ws = [];
        let cur = new Date(start);
        while (cur <= end) {
            ws.push(new Date(cur));
            cur.setDate(cur.getDate() + 7);
        }
        return {
            startDate: toIsoDate(start),
            endDate: toIsoDate(end),
            weeks: ws,
        };
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const r = await salesAPI.coverage({ start_date: startDate, end_date: endDate });
                setData(r.data);
            } catch (e) {
                console.error(e);
            } finally { setLoading(false); }
        })();
    }, [startDate, endDate]);

    // Build sites × week → set of days
    const grid = useMemo(() => {
        if (!data) return null;
        const map = {}; // site_id -> { weekIso(Mon): Set<date> }
        for (const [sid, dateIso] of data.pairs) {
            const monIso = mondayOf(dateIso);
            if (!map[sid]) map[sid] = {};
            if (!map[sid][monIso]) map[sid][monIso] = new Set();
            map[sid][monIso].add(dateIso);
        }
        return map;
    }, [data]);

    // Group weeks by ISO year (year of the Thursday of the week)
    const yearGroups = useMemo(() => {
        const groups = [];
        let current = null;
        for (let i = 0; i < weeks.length; i++) {
            const mon = weeks[i];
            const thu = new Date(mon);
            thu.setDate(thu.getDate() + 3);
            const y = thu.getFullYear();
            if (!current || current.year !== y) {
                current = { year: y, startIdx: i, nWeeks: 1 };
                groups.push(current);
            } else {
                current.nWeeks += 1;
            }
        }
        return groups;
    }, [weeks]);

    // Scroll to current week on first render
    useEffect(() => {
        if (!scrollerRef.current || !weeks.length) return;
        const todayMonday = mondayOf(toIsoDate(new Date()));
        const idx = weeks.findIndex(w => toIsoDate(w) === todayMonday);
        if (idx >= 0) {
            const cellW = 40; // approx
            scrollerRef.current.scrollLeft = Math.max(0, (idx - 4) * cellW);
        }
    }, [weeks, data]);

    if (loading || !data) {
        return (
            <Card><CardContent className="py-4 text-center text-slate-400 text-sm">
                <Loader2 className="w-4 h-4 mx-auto animate-spin" />
            </CardContent></Card>
        );
    }

    const sites = data.sites;

    return (
        <Card data-testid="coverage-grid">
            <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-500" />
                        Couverture hebdomadaire des ventes
                    </CardTitle>
                    <div className="flex items-center gap-2 text-[10px]">
                        <LegendDot color="bg-emerald-500" label="Complète (7 j)" />
                        <LegendDot color="bg-amber-400" label="Partielle (1-6 j)" />
                        <LegendDot color="bg-slate-200" label="Manquante" />
                        <LegendDot color="bg-blue-200" label="Semaine en cours" border />
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                <div ref={scrollerRef} className="overflow-x-auto overflow-y-hidden" data-testid="coverage-scroller">
                    <div className="min-w-max">
                        {/* Header row: years */}
                        <div className="flex border-b border-slate-200" data-testid="coverage-year-row">
                            <div className="w-28 flex-shrink-0 sticky left-0 bg-white border-r border-slate-200 py-0.5 px-2 text-[10px] font-semibold text-slate-500">
                                Année
                            </div>
                            {yearGroups.map((g) => {
                                const isCurrent = g.year === new Date().getFullYear();
                                return (
                                    <div
                                        key={g.year}
                                        className={`flex-shrink-0 text-center py-0.5 text-[11px] font-bold border-r border-slate-200 ${isCurrent ? 'bg-blue-100 text-blue-700' : 'bg-slate-50 text-slate-600'}`}
                                        style={{ width: `${g.nWeeks * 36}px` }}
                                        title={`${g.nWeeks} semaines en ${g.year}`}
                                        data-testid={`coverage-year-${g.year}`}
                                    >
                                        {g.year}
                                    </div>
                                );
                            })}
                        </div>
                        {/* Header row: weeks */}
                        <div className="flex">
                            <div className="w-28 flex-shrink-0 sticky left-0 bg-white border-r border-slate-200 py-1.5 px-2 text-[10px] font-semibold text-slate-500">
                                Site
                            </div>
                            {weeks.map((mon, i) => {
                                const isCurrent = sameWeek(mon, new Date());
                                const day = mon.getDate().toString().padStart(2, '0');
                                const month = (mon.getMonth() + 1).toString().padStart(2, '0');
                                const weekNum = isoWeekNumber(mon);
                                return (
                                    <div
                                        key={i}
                                        className={`w-9 flex-shrink-0 text-center py-1 text-[9px] ${isCurrent ? 'bg-blue-50 text-blue-700 font-bold' : 'text-slate-400'}`}
                                        title={`Semaine ${weekNum} — ${day}/${month}`}
                                    >
                                        <div>S{weekNum}</div>
                                        <div className="text-[8px]">{day}/{month}</div>
                                    </div>
                                );
                            })}
                        </div>
                        {/* One row per site */}
                        {sites.map(site => (
                            <div key={site.id} className="flex border-t border-slate-100">
                                <div className="w-28 flex-shrink-0 sticky left-0 bg-white border-r border-slate-200 py-1.5 px-2 text-[11px] font-medium text-slate-700">
                                    {site.name.replace('FE ', '')}
                                </div>
                                {weeks.map((mon, i) => {
                                    const monIso = toIsoDate(mon);
                                    const daysSet = grid[site.id]?.[monIso];
                                    const count = daysSet ? daysSet.size : 0;
                                    const status = count === 0 ? 'missing' : count >= 7 ? 'full' : 'partial';
                                    const isCurrent = sameWeek(mon, new Date());
                                    const colorClass = status === 'full'
                                        ? 'bg-emerald-500'
                                        : status === 'partial'
                                            ? 'bg-amber-400'
                                            : 'bg-slate-200';
                                    const sunday = new Date(mon);
                                    sunday.setDate(sunday.getDate() + 6);
                                    const sundayIso = toIsoDate(sunday);
                                    const clickable = status !== 'missing' && onCellClick;
                                    return (
                                        <div
                                            key={i}
                                            className={`w-9 flex-shrink-0 py-1 px-0.5 flex items-center justify-center ${isCurrent ? 'bg-blue-50' : ''}`}
                                            data-testid={`cov-${site.id}-${monIso}`}
                                        >
                                            <div
                                                onClick={clickable ? () => onCellClick(site.id, monIso, sundayIso) : undefined}
                                                className={`w-7 h-6 rounded ${colorClass} flex items-center justify-center text-[9px] font-bold text-white shadow-sm ${clickable ? 'cursor-pointer hover:ring-2 hover:ring-slate-400 transition-all' : ''}`}
                                                title={`${site.name} — Semaine ${isoWeekNumber(mon)} (${monIso}) — ${count}/7 jours${clickable ? ' (cliquez pour voir le détail)' : ''}`}
                                            >
                                                {status === 'partial' ? count : ''}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function LegendDot({ color, label, border }) {
    return (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <span className={`w-2.5 h-2.5 rounded ${color} ${border ? 'border border-blue-400' : ''}`} />
            <span className="text-slate-500">{label}</span>
        </span>
    );
}

// Helpers
function toIsoDate(d) { return d.toISOString().split('T')[0]; }
function mondayOf(dateIso) {
    const d = new Date(dateIso + 'T00:00:00');
    const dow = d.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    d.setDate(d.getDate() + diff);
    return toIsoDate(d);
}
function sameWeek(d1, d2) { return mondayOf(toIsoDate(d1)) === mondayOf(toIsoDate(d2)); }
function isoWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function todayStr() { return new Date().toISOString().split('T')[0]; }
function daysAgoStr(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; }
